import React, {useState, useEffect, useContext} from 'react';
import Modal from "react-modal";
import Web3 from "web3";
import Context from "../../../context/context";
import TransactionStatus from "../TxnStatus";
import Swal  from "sweetalert2";
import DAO from '../abi/Dao.json';
import Staking from '../abi/Staking.json';
import { formatUnits, parseUnits } from "ethers";
import { sendPostRequest } from '../../../helper/Helper';

const NewProposalModal = ({ modalIsOpen, closeModal }) => {
  const { account, onConnect, plsPrice, providers } = useContext(Context);  
  const [secondModalStatus, setSecondModalStatus] = useState(false);
  const [quickProposalStatus, setQuickProposalStatus] = useState(false);
  const [eligibleStatus, setEligibleStatus] = useState(false);
  const [proposalTitle, setProposalTitle] = useState('');
  const [proposalContent, setProposalContent] = useState('');
  const [fundsRequest, setFundsRequest] = useState('');
  const [twitter, setTwitter] = useState('');
  const [errors, setErrors] = useState('');
  const [availablePLS, setAvailablePLS] = useState(0);
  const [fundRequestLimit, setFundRequestLimit] = useState(100000000);

  const openSecondModalCustom = () => { 
      closeModal(); 
      setQuickProposalStatus(false);
      setSecondModalStatus(true);
  };
  const openSecondModalQuick = () => { 
      closeModal(); 
      setQuickProposalStatus(true);
      setSecondModalStatus(true);
  };
  const closeSecondModal = () => setSecondModalStatus(false);
  const afterSecondModalOpen = async () => {await getData();}; 

  const getData = async () => {
      const web3 = new Web3(providers);
      const daoContract = new web3.eth.Contract(DAO.ABI, DAO.Contract);
      const stakingContract = new web3.eth.Contract(Staking.ABI, Staking.Contract);

      const totalPLS = Number(formatUnits(await web3.eth.getBalance(DAO.Contract), 18));
      const lockedPLS = Number(formatUnits(await daoContract.methods.lockedAmount().call(), 18));
      const availablePLS = parseFloat(totalPLS - lockedPLS).toFixed(2);
      if (availablePLS > 100000000) {
          setFundRequestLimit(100000000);
      } else {
          setFundRequestLimit(availablePLS);
      }
      if (quickProposalStatus === true) {
          setProposalTitle('Buy & Burn + Liquidity Add');
          setProposalContent('Buy & Burn + Liquidity Add');
          if (availablePLS > 100000000) {
            setFundsRequest(100000000);
          } else {
            setFundsRequest(availablePLS);
          }
      } else {
          setProposalTitle('');
          setProposalContent('');
          setFundsRequest('');
      }
      setEligibleStatus(await stakingContract.methods.checkHighestStaker(account).call());
  }

  const MAX_TITLE_LEN = 200;
  const MAX_CONTENT_LEN = 5000;

  function isValidTitle(title) {
    return /^[a-zA-Z0-9\s.,!?()'"@#$&_\-:/]+$/.test(title);
  }

  function isValidContent(content) {
    return /^[a-zA-Z0-9\s.,!?()'"@#$&_\-:/\n\r]+$/.test(content);
  }

  function hasMaliciousPatterns(text) {
    const patterns = [
      /<\s*script\b/i,
      /<\/\s*script\s*>/i,
      /\bon\w+\s*=/i,            
      /javascript\s*:/i,
      /data\s*:\s*text\/html/i,
      /<\s*iframe\b/i,
      /<\s*img\b[^>]*onerror\s*=/i,
    ];
    return patterns.some((re) => re.test(text));
  }

  function validateProposalInputs(titleRaw, contentRaw) {
    const title = titleRaw.trim();
    const content = contentRaw.trim();

    if (!title) return "Title is required.";
    if (!content) return "Description is required.";

    if (title.length > MAX_TITLE_LEN) return "Title must be under 200 characters.";
    if (content.length > MAX_CONTENT_LEN) return "Description must be under 5000 characters.";

    if (hasMaliciousPatterns(title) || hasMaliciousPatterns(content)) {
      return "Proposal contains disallowed content.";
    }

    if (!isValidTitle(title)) {
      return "Title contains invalid characters.";
    }

    if (!isValidContent(content)) {
      return "Description contains invalid characters.";
    }

    return null;
  }

  const submitProposal = async () => {
    const validationError = validateProposalInputs(proposalTitle, proposalContent);
    if (validationError) {
      setErrors(validationError);
      return;
    }

    if (parseFloat(fundsRequest) <= 1) {
      setErrors("Fund request amount must be greater than 1 PLS.");
      return;
    }

    const web3 = new Web3(providers);
    const daoContract = new web3.eth.Contract(DAO.ABI, DAO.Contract);
    const amount = parseUnits(String(fundsRequest || "0"), 18);
    try {
        let tx = await daoContract.methods.propose(String(amount), proposalTitle).send({
            from: account
        }, async function(error, tx) {
            if (error) {
                Swal.fire({
                    title: "Error Found",
                    text: error.message,
                    icon: "error",
                    showCancelButton: false,
                    confirmButtonText: "Close",
                    customClass: {
                        confirmButton: "btn-danger"
                    }
                });
            } else {
                Swal.fire({
                    title: "Proposal Submission In Progress",
                    text: 'Please do not close or refresh page till file is uploaded.',
                    icon: "error",
                    showCancelButton: false,
                    confirmButtonText: "Close",
                    customClass: {
                        confirmButton: "btn-danger"
                    }
                });
            }
        });
        if (tx.status) {
            let proposalCount = await daoContract.methods.proposalCount().call();
            await uploadFileToDB(proposalCount)
        }
    } catch (error) {
        if (error.message.includes('eth_estimateGas')) {
            setErrors('Insufficient Funds For Transaction');
        } else if (error.message.includes('one live proposal per proposer')) {
            setErrors('Already submitted a proposal with active status');
        } else if (error.message.includes('min 1 PLS limit')) {
            setErrors('Fund request amount must be greater than 1 PLS.');
        } else {
            setErrors(error.message);
        }
    }
  }

  const uploadFileToDB = async (proposalCount) => {
      let propNum = parseInt(proposalCount);
      try {
          sendPostRequest({id: propNum, proposalTitle, proposalContent, twitter, walletAddress: account}, '/api/add-proposal');
          closeSecondModal();
          Swal.fire({
              title: "Proposal Submitted Successfully",
              text: 'Thanks for waiting',
              icon: "success",
              showCancelButton: false,
              confirmButtonText: "Close",
              customClass: {
                  confirmButton: "btn-danger"
              }
          });
      } catch (error) {
          closeSecondModal();
          Swal.fire({
              title: "Error Found",
              text: 'Something went wrong, please contact the owner',
              icon: "error",
              showCancelButton: false,
              confirmButtonText: "Close",
              customClass: {
                  confirmButton: "btn-danger"
              }
          });
      }
  }
 
  return (
  <>
  <Modal isOpen={modalIsOpen} onRequestClose={closeModal} contentLabel="Modal">
  <div className="My__Overlay">
    <div className="My__OverlayIn">
          <div className="container">
      <div className='bottomModal'>
        <div className="PTGCSEC popupcenter">
              <div className="d-flex justify-content-end borderclose">
                <button className="nobg closepopup reltiveClose d-flex align-items-center gap-1" onClick={closeModal}>
                  <span className="underline">Close</span>
                  <img src="./images/XCircle.svg" className="img-fluid" alt="close" />
                </button>
              </div>
              <div className="">
                <div className="row">
                  <div className="col-md-6 bordercol">
                    <button className="nobg w-100" onClick={openSecondModalQuick}>
                      <div className=" snstakes statsALL SRTOP ">
                        <div className="relative">
                          <div className="t">
                            <div className="d-flex justify-content-between">
                              <div className="iconSR">
                                <img className="img-fluid" src="./images/CaretCircleUpDown.svg"/>
                              </div>
                              <span className=" mybtn underline text-white"><span>Build Proposal</span> <img src="./images/ArrowCircleUpRight.svg" className="img-fluid m-0" /></span>
                            </div>
                            <div className="statBottom">
                              <h2 className="fHead text-start">Buy & Burn + Liquidity Add</h2>
                              <div className="perVal fInter d-flex align-items-center">
                                <span>Quick Proposal</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                  <div className="col-md-6 bordercol">
                    <button className="nobg w-100" onClick={openSecondModalCustom}>
                      <div className="snstakes statsALL SRTOP ">
                        <div className="relative">
                          <div className="t">
                            <div className="d-flex justify-content-between">
                              <div className="iconSR">
                                <img className="img-fluid" src="./images/CaretCircleUpDown.svg"/>
                              </div>
                              <span className=" mybtn underline text-white"><span>Build Proposal</span> <img src="./images/ArrowCircleUpRight.svg" className="img-fluid m-0" /></span>
                            </div>
                            <div className="statBottom">
                              <h2 className="fHead text-start">Custom</h2>
                              <div className="perVal fInter d-flex align-items-center">
                                <span>Quick Proposal</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </div>
        </div>
      </div>
    </Modal>
    <Modal isOpen={secondModalStatus} onRequestClose={closeSecondModal} onAfterOpen={afterSecondModalOpen}>
      <div className="My__Overlay">
        <div className="My__OverlayIn">
          <div className="container">
          <div className='secondbottomModal bottomModal'>
            <div className="PTGCSEC popupcenter">
              <div className="d-flex justify-content-between borderclose SRTOP closepopup reltiveClose">
                <div className="iconSR"><img className="img-fluid" src="./images/CaretCircleUpDown.svg"/></div>
                <button className="nobg  d-flex align-items-center gap-1" onClick={closeSecondModal}>
                  <span className="underline">Close</span>
                  <img src="./images/XCircle.svg" className="img-fluid" alt="close" />
                </button>
              </div>
              <div className="row m-0 borderBottomdesktop">
                <div className="col-sm-6 col-lg-8 borderBottom mobBorderBottomAdd1191 p-0  ">
                  <div className="colspce bordercol " style={{minHeight:'auto'}}>
                    <div className="perVal fInter d-flex align-items-center mb-1">
                      <span>Proposal Title</span>
                    </div>
                    <div className="perVal fInter d-flex align-items-center">
                      <input className="per2 w-100" type="text" placeholder="Buy & Burn + Liquidity Add" value={proposalTitle} onChange={(e) => setProposalTitle(e.target.value)}/>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-4 borderBottom mobBorderBottomAdd1191 p-0  ">
                  <div className="colspce bordercol " style={{minHeight:'auto'}}>
                    <div className="perVal fInter d-flex align-items-center mb-1">
                      <span>Wallet Connected</span>
                    </div>
                    <div className="perVal fInter d-flex align-items-center">
                      <span className="per2 w-100 wordbreak">{account}</span>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-8 p-0 mobBorderBottomAdd1191">
                  <div className="colspce bordercol " style={{minHeight:'auto'}}>
                    <div className="perVal fInter d-flex align-items-center mb-1">
                      <span>Proposal Content</span>
                    </div>
                    <div className="perVal fInter d-flex align-items-center">
                      <textarea className="per2 per3 w-100" type="text" placeholder="P1" rows='6' value={proposalContent} onChange={(e) => setProposalContent(e.target.value)}></textarea>
                    </div>
                  </div>
                </div>
                <div className="col-sm-6 col-lg-4   p-0  ">
                  <div className="colspce bordercol" style={{minHeight:'auto'}}>
                    <div className="perVal fInter d-flex align-items-center mb-1">
                      <span>Proposal For</span>
                    </div>
                    <div className="perVal fInter d-flex align-items-center">
                      <span>7 Days From Now</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="snstakes">
              <div className="lastport">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center flex-wrap">
                    <div className="amountSTaking lastporthei bb0 d-flex justify-content-between align-items-center gap-3 mobBorderRemove991">
                      <div className="d-flex gap-2 w-100">
                        <img className="img-fluid" src="./images/plusechain.png"/>
                        <input
                          type="text"
                          className="nobginput w-100"
                          placeholder='0.00'
                          value={
                            fundsRequest === "" || isNaN(Number(fundsRequest.toString().replace(/,/g, "")))
                              ? ""
                              : Number(fundsRequest.toString().replace(/,/g, "")).toLocaleString("en-US", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })
                          }
                          onChange={async (e) => {
                            let raw = e.target.value.replace(/,/g, "");

                            if (raw === "") {
                              await setFundsRequest("");
                              return;
                            }

                            if (!/^\d*(\.\d{0,2})?$/.test(raw)) return;
                            const num = Math.min(Number(raw), Number(fundRequestLimit));
                            await setFundsRequest(num);
                          }}
                        />
                      </div>
                    </div>
                    <div className="px-3">
                      <div className="dollorVal f28 mt-0">
                        <span className="dollor dollor1">$</span>
                        <span className="val f28">{parseFloat(fundsRequest * plsPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  </div>
                  <div className="amountSTaking border0">
                  {
                    eligibleStatus ?
                    <>
                    <button className="mb_down1 borderbtn mybtn d-flex justify-content-between align-items-center" onClick={submitProposal}>
                      <span>Publish Proposal</span> 
                      <img className="img-fluid" src="./images/circle_arrow.svg" />
                    </button>
                    </>
                    :
                    <>
                    <button className="mb_down1 borderbtn mybtn d-flex justify-content-between align-items-center" disabled>
                      <span>Publish Proposal</span> 
                      <img className="img-fluid" src="./images/circle_arrow.svg" />
                    </button>
                    </>
                  }
                </div>
                </div>
              </div>
              {
                errors !=="" ?
                <>
                  <div className="error mt-2 "><small>Note: {errors}</small></div>
                </>
                :
                <>
                </>
              }
              {
                !eligibleStatus ?
                <>
                  <div className="error mt-2 "><small>Note: You are not eligible to submit a proposal; only the top 100 stakers can</small></div>
                </>
                :
                <>
                </>
              }
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </Modal>
    </>
  );
};

export default NewProposalModal;